import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Github, Mail, Key, Apple, Phone, Smartphone } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../hooks/useAuth';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SuperToolLogo from '../components/SuperToolLogo';
import AppShowcase from '../components/AppShowcase';
import PricingSection from '../components/PricingSection';
import FeaturesSection from '../components/FeaturesSection';
import FAQSection from '../components/FAQSection';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import Header from '../components/Header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { login, register, user, setUser } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('main'); // main, email, phone, sso
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', phone: '' });
  const [ideaInput, setIdeaInput] = useState('');
  const authRef = useRef(null);

  React.useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  useEffect(() => {
    fetch(`${API_URL}/api/track/visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ page: '/', referrer: document.referrer })
    }).catch(() => {});
  }, []);

  const scrollTo = (section) => {
    if (section === 'auth') { authRef.current?.scrollIntoView({ behavior: 'smooth' }); return; }
    document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSocialLogin = async (provider) => {
    const redirectBase = process.env.REACT_APP_APP_URL || window.location.origin;
    try {
      if (provider === 'Google') {
        const res = await fetch(`${API_URL}/api/auth/google/login`);
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else if (provider === 'GitHub') {
        const res = await fetch(`${API_URL}/api/auth/github/login`);
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else if (provider === 'Facebook') {
        const res = await fetch(`${API_URL}/api/auth/facebook/login`);
        const data = await res.json();
        if (data.url) window.location.href = data.url;
      } else if (provider === 'Twitter') {
        const res = await fetch(`${API_URL}/api/auth/twitter/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ redirect_uri: `${redirectBase}/auth/callback/twitter` }) });
        const data = await res.json();
        if (data.code_verifier) sessionStorage.setItem('twitter_code_verifier', data.code_verifier);
        if (data.state) sessionStorage.setItem('twitter_oauth_state', data.state);
        if (data.url) window.location.href = data.url;
      } else if (provider === 'Apple') {
        toast({ title: 'Coming Soon', description: 'Apple Sign-In will be available soon.' });
      }
    } catch {
      toast({ title: 'Error', description: `${provider} login failed`, variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = isLogin ? await login(formData.email, formData.password) : await register(formData.name, formData.email, formData.password);
      if (result.success) {
        toast({ title: 'Success!', description: isLogin ? 'Welcome back!' : 'Account created!' });
        navigate('/dashboard');
      } else {
        toast({ title: 'Error', description: result.error, variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'An unexpected error occurred', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  const handleIdeaSubmit = () => {
    if (!ideaInput.trim()) return;
    sessionStorage.setItem('supertool_idea', ideaInput.trim());
    authRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white relative overflow-x-hidden">
      {/* Subtle background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute top-0 left-1/3 w-[800px] h-[800px] bg-blue-50/60 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-50/50 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10">
      <Header onScrollTo={scrollTo} />

      {/* Hero — Emergent Style */}
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-6 flex items-start gap-16 py-12 min-h-[calc(100vh-80px)]">
          {/* Left — Auth */}
          <div className="w-full max-w-md shrink-0">
            {/* Idea Input — Emergent "Describe your idea" */}
            <div className="mb-8">
              <div className="flex justify-center mb-6">
                <SuperToolLogo size={52} animated={true} />
              </div>
              <div className="relative">
                <Input
                  value={ideaInput}
                  onChange={(e) => setIdeaInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleIdeaSubmit()}
                  placeholder="Describe your idea"
                  className="h-14 rounded-2xl bg-gray-50 border-gray-200 text-gray-900 text-base placeholder:text-gray-400 pl-5 pr-12 focus:border-blue-400 focus:ring-blue-400/20"
                  data-testid="idea-input"
                />
                <button onClick={handleIdeaSubmit} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-colors" data-testid="idea-submit">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                </button>
              </div>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center leading-tight">
              Build websites & apps{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">with AI</span>
            </h1>

            {/* Auth Card — Emergent style */}
            <div ref={authRef} className="space-y-3" data-testid="auth-card">
              {/* Google */}
              <Button data-testid="google-login-btn" onClick={() => handleSocialLogin('Google')}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 h-[52px] rounded-2xl flex items-center justify-center gap-3 text-[15px] font-medium border border-gray-200 hover:border-gray-300 shadow-sm transition-all">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>

              {/* Facebook + Apple row */}
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => handleSocialLogin('Facebook')} variant="outline"
                  className="h-[52px] rounded-2xl border-gray-200 bg-white hover:bg-gray-50 gap-2 text-sm font-medium text-gray-700" data-testid="facebook-login-btn">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </Button>
                <Button onClick={() => handleSocialLogin('Apple')} variant="outline"
                  className="h-[52px] rounded-2xl border-gray-200 bg-white hover:bg-gray-50 gap-2 text-sm font-medium text-gray-700" data-testid="apple-login-btn">
                  <Apple className="w-5 h-5" />
                  Apple
                </Button>
              </div>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-5 text-sm text-gray-400 font-medium">OR</span></div>
              </div>

              {/* Email, Phone, SSO */}
              <div className="grid grid-cols-1 gap-2.5">
                <Button onClick={() => { setAuthMode('email'); setShowAuthModal(true); }} variant="outline"
                  className="w-full h-[52px] rounded-2xl border-gray-200 bg-white hover:bg-gray-50 gap-3 text-[15px] font-medium text-gray-700 justify-center" data-testid="email-login-btn">
                  <Mail className="w-5 h-5 text-gray-500" />
                  Continue with Email
                </Button>
                <Button onClick={() => { setAuthMode('phone'); setShowAuthModal(true); }} variant="outline"
                  className="w-full h-[52px] rounded-2xl border-gray-200 bg-white hover:bg-gray-50 gap-3 text-[15px] font-medium text-gray-700 justify-center" data-testid="phone-login-btn">
                  <Smartphone className="w-5 h-5 text-gray-500" />
                  Continue with Phone
                </Button>
              </div>

              <p className="text-center text-xs text-gray-400 mt-4">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-gray-500 hover:text-blue-600 underline">Terms of Service</a>{' '}and{' '}
                <a href="/privacy" className="text-gray-500 hover:text-blue-600 underline">Privacy Policy</a>.
              </p>

              <Button onClick={() => { setAuthMode('sso'); setShowAuthModal(true); }} variant="ghost"
                className="w-full text-sm text-gray-400 hover:text-gray-600 gap-2 mt-1" data-testid="sso-login-btn">
                <Key className="w-4 h-4" />
                Continue with SSO
              </Button>
            </div>
          </div>

          {/* Right — Showcase & Stats */}
          <div className="hidden lg:flex flex-col flex-1 pt-8">
            {/* YC Badge + Stats */}
            <div className="flex items-center gap-6 mb-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-full">
                <span className="text-orange-600 font-bold text-lg">Y</span>
                <span className="text-gray-700 text-sm font-medium">Combinator S24</span>
              </div>
              <div>
                <span className="text-2xl font-bold text-gray-900">10M+</span>
                <span className="text-gray-500 text-sm ml-2">users worldwide building & launching real applications in minutes.</span>
              </div>
            </div>

            {/* App Showcase Carousel */}
            <AppShowcase />
          </div>
        </div>
      </main>

      {/* Features, Pricing, FAQ, CTA, Footer */}
      <div id="features"><FeaturesSection /></div>
      <div id="pricing"><PricingSection /></div>
      <div id="faq"><FAQSection /></div>
      <CTABanner onGetStarted={() => scrollTo('auth')} />
      <Footer />
      </div>

      {/* Auth Modal — Email / Phone / SSO */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md bg-white border border-gray-200 shadow-xl rounded-2xl" aria-describedby="auth-modal-desc">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center text-gray-900">
              {authMode === 'phone' ? 'Continue with Phone' : authMode === 'sso' ? 'SSO Login' : (isLogin ? 'Welcome Back' : 'Create Account')}
            </DialogTitle>
            <p id="auth-modal-desc" className="sr-only">Authentication form</p>
          </DialogHeader>

          {authMode === 'phone' ? (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Phone Number</Label>
                <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 (555) 000-0000"
                  className="mt-1 h-12 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl" data-testid="phone-input" />
              </div>
              <Button className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl" onClick={() => toast({ title: 'Coming Soon', description: 'Phone authentication will be available soon.' })}>
                Send Code
              </Button>
              <p className="text-center text-xs text-gray-400">{"We'll send a verification code to your phone."}</p>
            </div>
          ) : authMode === 'sso' ? (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-700">Work Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@company.com"
                  className="mt-1 h-12 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl" data-testid="sso-email-input" />
              </div>
              <Button className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl" onClick={() => toast({ title: 'Coming Soon', description: 'SSO login will be available soon.' })}>
                Continue with SSO
              </Button>
              <p className="text-center text-xs text-gray-400">{"We'll redirect you to your organization's login page."}</p>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div>
                    <Label htmlFor="name" className="text-gray-700">Full Name</Label>
                    <Input id="name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" required={!isLogin}
                      className="mt-1 h-12 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl" />
                  </div>
                )}
                <div>
                  <Label htmlFor="email" className="text-gray-700">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="you@example.com" required
                    className="mt-1 h-12 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-gray-700">Password</Label>
                  <Input id="password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="********" required
                    className="mt-1 h-12 bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400 rounded-xl" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white rounded-xl">
                  {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
                </Button>
              </form>
              <div className="text-center text-sm">
                <button type="button" onClick={() => { setIsLogin(!isLogin); setFormData({ name: '', email: '', password: '', phone: '' }); }}
                  className="text-blue-600 hover:text-blue-500 font-medium">
                  {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
